/**
 * ------------------------------------------------------------------------------------------------------------
 * ARCHITECTURAL NOTE:
 * KHÔNG sử dụng file index.ts (Barrel) trong thư mục @core để tránh Circular Dependency (NG0200).
 * Luôn import trực tiếp từ file nguồn.
 * ------------------------------------------------------------------------------------------------------------
 */
import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from "@angular/common";

/**
 * Class xử lý dữ liệu lưu Local Storage
 *    - Dữ liệu sẽ lưu theo domain.
 *    - Dữ liệu chỉ bị xóa khi sử dụng event để xóa hoặc xóa cache trình duyệt
 * */
@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  constructor(@Inject(PLATFORM_ID) private platformId: object) {
  }

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  getPure(key: string): any {
    if (this.isBrowser()) {
      const val = localStorage.getItem(key);
      if (val === 'undefined' || val === null) {
        return '';
      }
      return val;
    }
    return '';
  }

  setPure(key: string, value: any): void {
    if (this.isBrowser()) {
      localStorage.setItem(key, value);
    }
  }

  get(key: string): any {
    if (this.isBrowser()) {
      const val = localStorage.getItem(key);
      if (val === 'undefined' || val === null) {
        return '';
      }
      return JSON.parse(val || '{}') || {};
    }
    return {};
  }

  set(key: string, value: any): void {
    if (this.isBrowser()) {
      localStorage.setItem(key, JSON.stringify(value));
    }
  }

  has(key: string): boolean {
    if (this.isBrowser()) {
      return !!localStorage.getItem(key);
    }
    return false;
  }

  remove(key: string): void {
    if (this.isBrowser()) {
      localStorage.removeItem(key);
    }
  }

  clear(): void {
    if (this.isBrowser()) {
      localStorage.clear();
    }
  }

  clearLocalStorageByPrefix(prefix: string): void {
    if (this.isBrowser()) {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          localStorage.removeItem(key);
        }
      }
    }
  }
}


/**
 * Class xử lý dữ liệu lưu tạm để xử lý trên RAM.
 * */
@Injectable({
  providedIn: 'any',
})
export class MemoryStorageService {
  private store: { [k: string]: string } = {};

  get(key: string) {
    return JSON.parse(this.store[key] || '{}') || {};
  }

  set(key: string, value: any): boolean {
    this.store[key] = JSON.stringify(value);
    return true;
  }

  getPure(key: string): any {
    return this.store[key] ? this.store[key] : null;
  }

  setPure(key: string, value: any) {
    this.store[key] = value;
  }


  has(key: string): boolean {
    return !!this.store[key];
  }

  remove(key: string) {
    delete this.store[key];
  }

  clear() {
    this.store = {};
  }
}
